import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Search, Edit, Eye, MoreHorizontal, Trash2, Download, FileSpreadsheet } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { BillForm } from './BillForm';
import { exportBillsToExcel, parseExcelFile, convertExcelToBillData } from '@/utils/excelUtils';
import { billsService } from '@/services/billsService';

const statusColors = {
  draft: 'secondary',
  entered: 'default', 
  partially_paid: 'outline',
  paid: 'default',
  void: 'destructive'
} as const;

const statusLabels = {
  draft: 'Draft',
  entered: 'Entered',
  partially_paid: 'Partially Paid', 
  paid: 'Paid',
  void: 'Void'
} as const;

// Helper function to format currency
const formatCurrency = (amount: number, currency: string) => {
  const locales: Record<string, string> = {
    'EUR': 'el-GR',
    'USD': 'en-US', 
    'RON': 'ro-RO',
    'PLN': 'pl-PL'
  };
  
  return new Intl.NumberFormat(locales[currency] || 'en-US', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

export const BillsList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [supplierFilter, setSupplierFilter] = useState<string>('all');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: bills, isLoading, refetch } = useQuery({
    queryKey: ['bills', searchTerm, statusFilter, supplierFilter],
    queryFn: async () => {
      let query = supabase
        .from('bills')
        .select(`
          *,
          suppliers:supplier_id (
            id,
            display_name
          )
        `)
        .order('created_at', { ascending: false });

      if (searchTerm) {
        query = query.or(`description.ilike.%${searchTerm}%,bill_number.ilike.%${searchTerm}%`);
      }

      if (statusFilter && statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (supplierFilter && supplierFilter !== 'all') {
        query = query.eq('supplier_id', supplierFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    }
  });

  const deleteBillMutation = useMutation({
    mutationFn: async (billId: string) => {
      const { error } = await supabase
        .from('bills')
        .delete()
        .eq('id', billId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Bill deleted successfully" });
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting bill",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: async (billData: any[]) => {
      const results = { updated: 0, created: 0, errors: [] as string[] };
      
      for (const bill of billData) {
        try {
          if (bill.id) {
            // Update existing bill
            await billsService.updateBill(bill);
            results.updated++;
          } else {
            // Create new bill
            const { id, ...createData } = bill;
            await billsService.createBill(createData);
            results.created++;
          }
          } catch (error: any) {
            results.errors.push(`Error processing ${bill.bill_number || 'bill'}: ${error.message}`);
          }
      }
      
      return results;
    },
    onSuccess: (results) => {
      refetch();
      queryClient.invalidateQueries({ queryKey: ['all-bills'] });
      
      let message = '';
      if (results.updated > 0) message += `Updated ${results.updated} bills. `;
      if (results.created > 0) message += `Created ${results.created} bills. `;
      
      toast({ 
        title: "Excel update completed", 
        description: message.trim() || "No changes were made",
        variant: results.errors.length > 0 ? "destructive" : "default"
      });
      
      if (results.errors.length > 0) {
        console.error('Bulk update errors:', results.errors);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error updating bills",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Query for all bills (for export)
  const { data: allBills } = useQuery({
    queryKey: ['all-bills'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bills')
        .select(`
          *,
          suppliers:supplier_id (
            id,
            display_name
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    }
  });

  const { data: suppliers } = useQuery({
    queryKey: ['suppliers-for-filter'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, display_name')
        .eq('is_active', true)
        .order('display_name');
      
      if (error) throw error;
      return data;
    }
  });

  const handleEnterBill = async (billId: string) => {
    try {
      // Update status to entered
      const { error: updateError } = await supabase
        .from('bills')
        .update({ status: 'entered' })
        .eq('id', billId);

      if (updateError) throw updateError;

      // Create GL posting
      const { error: glError } = await supabase.rpc('create_bill_gl_posting', {
        bill_id_param: billId
      });

      if (glError) throw glError;

      toast({ title: "Bill entered successfully" });
      refetch();
    } catch (error: any) {
      toast({
        title: "Error entering bill",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleVoidBill = async (billId: string) => {
    try {
      // Create void GL posting first
      const { error: glError } = await supabase.rpc('void_bill_gl_posting', {
        bill_id_param: billId
      });

      if (glError) throw glError;

      // Update status to void
      const { error: updateError } = await supabase
        .from('bills')
        .update({ status: 'void' })
        .eq('id', billId);

      if (updateError) throw updateError;

      toast({ title: "Bill voided successfully" });
      refetch();
    } catch (error: any) {
      toast({
        title: "Error voiding bill",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleExport = () => {
    if (!allBills || allBills.length === 0) {
      toast({
        title: "No data to export",
        description: "There are no bills to export",
        variant: "destructive"
      });
      return;
    }
    
    exportBillsToExcel(allBills, `bills_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast({ title: "Excel file downloaded successfully" });
  };

  const handleExcelUpdate = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const excelData = await parseExcelFile(file);
      const billData = convertExcelToBillData(excelData);
      
      if (billData.length === 0) {
        toast({
          title: "No valid data found",
          description: "The Excel file does not contain valid bill data",
          variant: "destructive"
        });
        return;
      }

      bulkUpdateMutation.mutate(billData);
    } catch (error: any) {
      toast({
        title: "Error reading Excel file",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Bills</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExport} disabled={!allBills || allBills.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
            disabled={bulkUpdateMutation.isPending}
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Excel Update
          </Button>
          <BillForm onSuccess={refetch} />
        </div>
      </div>

      {/* Hidden file input for Excel update */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleExcelUpdate}
      />

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search bills..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="entered">Entered</SelectItem>
                <SelectItem value="partially_paid">Partially Paid</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="void">Void</SelectItem>
              </SelectContent>
            </Select>
            <Select value={supplierFilter} onValueChange={setSupplierFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Supplier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Suppliers</SelectItem>
                {suppliers?.map((supplier) => (
                  <SelectItem key={supplier.id} value={supplier.id}>
                    {supplier.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Loading bills...</div>
          ) : bills && bills.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bill Number</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Issue Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bills.map((bill) => (
                  <TableRow key={bill.id}>
                    <TableCell className="font-mono text-sm">
                      {bill.bill_number || `Bill #${bill.id.slice(-8)}`}
                    </TableCell>
                    <TableCell>
                      {bill.suppliers?.display_name || 'Unknown Supplier'}
                    </TableCell>
                    <TableCell>
                      {formatCurrency(bill.grand_total || bill.amount, bill.currency)}
                    </TableCell>
                    <TableCell>
                      {bill.issue_date ? format(new Date(bill.issue_date), "MMM dd, yyyy") : "—"}
                    </TableCell>
                    <TableCell>
                      {bill.due_date ? format(new Date(bill.due_date), "MMM dd, yyyy") : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusColors[bill.status as keyof typeof statusColors]}>
                        {statusLabels[bill.status as keyof typeof statusLabels]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                         <DropdownMenuContent align="end">
                       <DropdownMenuItem>
                         <Eye className="h-4 w-4 mr-2" />
                         View
                       </DropdownMenuItem>
                       <DropdownMenuItem onClick={() => window.location.href = `/xero-lite/bills/${bill.id}`}>
                         <Edit className="h-4 w-4 mr-2" />
                         Edit
                       </DropdownMenuItem>
                           {bill.status === 'draft' && (
                             <DropdownMenuItem onClick={() => handleEnterBill(bill.id)}>
                               Enter Bill
                             </DropdownMenuItem>
                           )}
                           {bill.status === 'entered' && (
                             <DropdownMenuItem 
                               onClick={() => handleVoidBill(bill.id)}
                               className="text-destructive"
                             >
                               Void Bill
                             </DropdownMenuItem>
                           )}
                           <AlertDialog>
                             <AlertDialogTrigger asChild>
                               <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                 <Trash2 className="h-4 w-4 mr-2" />
                                 Delete
                               </DropdownMenuItem>
                             </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Bill</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete "{bill.bill_number || `Bill #${bill.id.slice(-8)}`}"? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                               <AlertDialogFooter>
                                 <AlertDialogCancel>Cancel</AlertDialogCancel>
                                 <AlertDialogAction 
                                   onClick={() => deleteBillMutation.mutate(bill.id)}
                                   className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                 >
                                   Delete
                                 </AlertDialogAction>
                               </AlertDialogFooter>
                             </AlertDialogContent>
                           </AlertDialog>
                         </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No bills found. Create your first bill to get started.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};